import { Fragment, jsx, jsxs } from 'react/jsx-runtime'
import { type ComponentProps, type ReactNode } from 'react'
import type { ElementContent } from 'hast'
import type { ExtraProps } from 'react-markdown'
import { toJsxRuntime } from 'hast-util-to-jsx-runtime'
import { twMerge } from 'tailwind-merge'

import { getLowlight } from '../lowlight'
import { classNames, extractText, isElement, toLines } from '../hast'
import { CopyButton } from './copy-button'

interface CodeBlockProps extends ComponentProps<'pre'>, ExtraProps {
  /** A gutter of line numbers, sticky so long lines scroll under it. */
  lineNumbers?: boolean
}

function render(children: Array<ElementContent>): ReactNode {
  return toJsxRuntime({ type: 'root', children }, { Fragment, jsx, jsxs })
}

/** The same shared lowlight registry the editor's code blocks use, so both highlight identically. */
function highlight(language: string, code: string): Array<ElementContent> {
  const lowlight = getLowlight()
  if (language && lowlight.registered(language)) {
    try {
      return lowlight
        .highlight(language, code)
        .children.filter(
          (node): node is ElementContent => node.type === 'element' || node.type === 'text' || node.type === 'comment',
        )
    } catch (error) {
      console.error('[Markdown] failed to highlight code block:', error)
    }
  }
  return [{ type: 'text', value: code }]
}

/**
 * A code block is a figure: a head that names the language and offers a copy, then the code. Every
 * line is its own row when numbered, so the numbers cannot drift out of step with the code, and the
 * number is `aria-hidden` and unselectable, so a copy or a screen reader gets the code alone.
 */
function CodeBlock({ node, className, children, lineNumbers = false, ...props }: CodeBlockProps) {
  const code = node?.children.find((child) => isElement(child, 'code'))

  if (!code) return <pre {...props}>{children}</pre>

  const language = classNames(code)
    .find((name) => name.startsWith('language-'))
    ?.slice('language-'.length)
  const raw = extractText(code).replace(/\n$/, '')
  const tree = highlight(language ?? '', raw)
  const languageClass = language ? `hljs language-${language}` : 'hljs'

  return (
    <figure
      data-slot="markdown-code-block"
      data-language={language}
      data-numbered={lineNumbers || undefined}
      className={twMerge('md-code-block', className)}
    >
      <figcaption className="md-code-head">
        <span className="type-ui-xs text-foreground-secondary">{language || 'text'}</span>
        <CopyButton text={raw} />
      </figcaption>
      {/* Focusable so a keyboard user can scroll a long line. */}
      <div className="md-code-scroll" tabIndex={0} role="region" aria-label={`${language || 'Code'} snippet`}>
        <pre {...props}>
          <code className={languageClass}>
            {lineNumbers
              ? toLines(tree).map((line, index) => (
                  <span key={index} className="md-code-line">
                    <span className="md-code-num" aria-hidden>
                      {index + 1}
                    </span>
                    <span className="md-code-text">{line.length ? render(line) : ' '}</span>
                  </span>
                ))
              : render(tree)}
          </code>
        </pre>
      </div>
    </figure>
  )
}

CodeBlock.displayName = 'Markdown.CodeBlock'

export { CodeBlock }
export type { CodeBlockProps }
